/**
 * Poll for a DOM element until it appears or a deadline passes (#674).
 *
 * The workspace explorer reveals the active file by expanding its ancestor
 * folders and scrolling its row into view. On a COLD load the tree, its linked
 * folders, and each intermediate folder's lazily-fetched children all arrive
 * asynchronously, so the target row can render well after the reveal was
 * scheduled. The old poll gave up after a fixed ~1s (20 × 50ms) and, because the
 * reveal had already been recorded as "done", never retried — the row rendered
 * un-selected and stayed that way. This waits long enough for that cascade to
 * finish (or a generous absolute deadline), resolving the element when it
 * appears so the caller can select + scroll it, or `null` on deadline/cancel so
 * the caller leaves the reveal un-recorded and a later change can retry.
 *
 * `find` is injected (the DOM query) so this stays pure and unit-testable with
 * fake timers. `cancel` lets a superseding reveal (a newer active file) abort a
 * stale poll — checked on every tick, so a cancelled poll never selects the old
 * target.
 */
export function waitForElement(
  find: () => HTMLElement | null,
  opts: { intervalMs?: number; deadlineMs?: number; cancel?: { cancelled: boolean } } = {},
): Promise<HTMLElement | null> {
  const intervalMs = opts.intervalMs ?? 50
  const deadlineMs = opts.deadlineMs ?? 10_000
  const cancel = opts.cancel
  const start = Date.now()
  return new Promise((resolve) => {
    const tick = () => {
      if (cancel?.cancelled) return resolve(null)
      const el = find()
      if (el) return resolve(el)
      if (Date.now() - start >= deadlineMs) return resolve(null)
      setTimeout(tick, intervalMs)
    }
    tick()
  })
}
